import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SesionesTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, sessions, attendance, enrollment } = userData;
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusBadge = (status) => {
    const styles = {
      live: 'bg-green-500/20 text-green-400 border-green-500/30',
      upcoming: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      replay: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusIcon = (status) => {
    return {
      live: '🔴',
      upcoming: '📅',
      replay: '📼',
    }[status] || '📺';
  };

  const attendanceMap = {};
  if (attendance) {
    attendance.forEach(att => {
      attendanceMap[att.session_id] = att;
    });
  }

  const handleMarkAttendance = async (sessionId, attended) => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/sessions/${sessionId}/attendance`,
        { attended },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert(`Asistencia marcada: ${attended ? 'Presente' : 'Ausente'}`);
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al marcar asistencia');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendReminder = async (sessionId) => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/notify`,
        {
          template_id: 'session_reminder',
          params: { session_id: sessionId }
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Recordatorio enviado por email');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar recordatorio');
    } finally {
      setIsProcessing(false);
    }
  };

  const copySessionLink = (session) => {
    // Generate session link based on status
    let link = '';
    if (enrollment?.status === 'active') {
      if (session.status === 'live') {
        link = `https://scholar-liberty.preview.emergentagent.com/sessions/live/${session.id}`;
      } else if (session.status === 'replay') {
        link = `https://scholar-liberty.preview.emergentagent.com/sessions/replay/${session.id}`;
      }
    } else {
      link = 'Usuario debe activar su cuenta primero';
    }
    
    navigator.clipboard.writeText(link);
    alert('Link copiado al portapapeles');
  };

  // Calculate stats
  const totalSessions = sessions?.length || 0;
  const attendedSessions = attendance?.filter(a => a.attended).length || 0;
  const missedSessions = attendance?.filter(a => !a.attended).length || 0;
  const attendanceRate = totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0;

  // Group sessions by status
  const liveSessions = sessions?.filter(s => s.status === 'live') || [];
  const upcomingSessions = sessions?.filter(s => s.status === 'upcoming') || [];
  const replaySessions = sessions?.filter(s => s.status === 'replay') || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-gray-400 text-sm mb-1">Total Sesiones</div>
          <div className="text-2xl font-bold text-white">{totalSessions}</div>
        </div>
        <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
          <div className="text-gray-400 text-sm mb-1">Asistidas</div>
          <div className="text-2xl font-bold text-green-400">{attendedSessions}</div>
        </div>
        <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
          <div className="text-gray-400 text-sm mb-1">Ausencias</div>
          <div className="text-2xl font-bold text-red-400">{missedSessions}</div>
        </div>
        <div className="bg-[#D4AF37]/10 rounded-lg p-4 border border-[#D4AF37]/30">
          <div className="text-gray-400 text-sm mb-1">% Asistencia</div>
          <div className="text-2xl font-bold text-[#D4AF37]">{attendanceRate}%</div>
        </div>
      </div>

      {/* Attendance Alert */}
      {attendanceRate < 50 && totalSessions > 3 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <h4 className="text-red-400 font-semibold mb-1">⚠️ Baja Asistencia</h4>
          <p className="text-sm text-red-300">
            El usuario tiene menos del 50% de asistencia. Considera enviar un recordatorio o contactarlo.
          </p>
        </div>
      )}

      {/* Live Sessions */}
      {liveSessions.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            🔴 EN VIVO AHORA <span className="text-xs text-gray-400">({liveSessions.length})</span>
          </h4>
          <div className="space-y-3">
            {liveSessions.map((session) => {
              const att = attendanceMap[session.id];
              return (
                <SessionCard
                  key={session.id}
                  session={session}
                  attendance={att}
                  onMarkAttendance={handleMarkAttendance}
                  onSendReminder={handleSendReminder}
                  onCopyLink={copySessionLink}
                  isProcessing={isProcessing}
                  userStatus={enrollment?.status}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            📅 PRÓXIMAS SESIONES <span className="text-xs text-gray-400">({upcomingSessions.length})</span>
          </h4>
          <div className="space-y-3">
            {upcomingSessions.map((session) => {
              const att = attendanceMap[session.id];
              return (
                <SessionCard
                  key={session.id}
                  session={session}
                  attendance={att}
                  onMarkAttendance={handleMarkAttendance}
                  onSendReminder={handleSendReminder}
                  onCopyLink={copySessionLink}
                  isProcessing={isProcessing}
                  userStatus={enrollment?.status}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Replay Sessions */}
      {replaySessions.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            📼 REPLAYS DISPONIBLES <span className="text-xs text-gray-400">({replaySessions.length})</span>
          </h4>
          <div className="space-y-3">
            {replaySessions.slice(0, 10).map((session) => {
              const att = attendanceMap[session.id];
              return (
                <SessionCard
                  key={session.id}
                  session={session}
                  attendance={att}
                  onMarkAttendance={handleMarkAttendance}
                  onSendReminder={handleSendReminder}
                  onCopyLink={copySessionLink}
                  isProcessing={isProcessing}
                  userStatus={enrollment?.status}
                />
              );
            })}
          </div>
        </div>
      )}

      {totalSessions === 0 && (
        <div className="text-center py-12 bg-black/30 rounded-lg border border-[#1a4d5c]/30">
          <p className="text-gray-400">No hay sesiones programadas</p>
        </div>
      )}
    </div>
  );
}

// Session Card Component
function SessionCard({ session, attendance, onMarkAttendance, onSendReminder, onCopyLink, isProcessing, userStatus }) {
  const getStatusBadge = (status) => {
    const styles = {
      live: 'bg-green-500/20 text-green-400 border-green-500/30',
      upcoming: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      replay: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusIcon = (status) => {
    return {
      live: '🔴',
      upcoming: '📅',
      replay: '📼',
    }[status] || '📺';
  };

  return (
    <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30 hover:bg-[#1a4d5c]/10 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Session Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{getStatusIcon(session.status)}</span>
            <div>
              <h4 className="text-white font-semibold">{session.title}</h4>
              <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                <span>📚 {session.pilar || 'General'}</span>
                <span>📅 {new Date(session.date || session.start_at).toLocaleDateString('es-ES')}</span>
                <span>⏱️ {session.duration || 90} min</span>
              </div>
            </div>
          </div>
          
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(session.status)}`}>
            {session.status.toUpperCase()}
          </span>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Attendance Status */}
          {attendance ? (
            <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
              attendance.attended ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {attendance.attended ? '✅ Asistió' : '❌ Ausente'}
              {attendance.joined_at && (
                <div className="text-xs opacity-70 mt-1">
                  {new Date(attendance.joined_at).toLocaleTimeString('es-ES')}
                </div>
              )}
            </div>
          ) : (
            <div className="px-3 py-2 rounded-lg bg-gray-500/20 text-gray-400 text-sm">
              Sin registro
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => onMarkAttendance(session.id, true)}
              disabled={isProcessing}
              className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-xs disabled:opacity-50"
              title="Marcar presente"
            >
              ✓
            </button>
            <button
              onClick={() => onMarkAttendance(session.id, false)}
              disabled={isProcessing}
              className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs disabled:opacity-50"
              title="Marcar ausente"
            >
              ✗
            </button>
          </div>

          {/* Additional Actions */}
          <div className="flex gap-2">
            {session.status === 'upcoming' && (
              <button
                onClick={() => onSendReminder(session.id)}
                disabled={isProcessing}
                className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded text-xs disabled:opacity-50"
                title="Enviar recordatorio"
              >
                📧
              </button>
            )}
            {(session.status === 'live' || session.status === 'replay') && userStatus === 'active' && (
              <button
                onClick={() => onCopyLink(session)}
                className="px-3 py-1 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded text-xs"
                title="Copiar link"
              >
                🔗
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}